import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { ComplianceApi } from 'entities/Compliance'
import { PhoneApi, AssignNumberCommonStore } from 'entities/Phone'
import { type IResponseNumber } from 'entities/Phone/api/types'
import { Phone } from 'entities/Phone/model/Phone'
import { ChooseNumberTabsStore } from 'entities/Phone/ui/ChooseNumberTabs/store/ChooseNumberTabsStore'
import { ITabsEnum } from 'entities/Phone/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type TollFreeStepsStore } from 'pages/settings/pages/compliance/pages/tollFree/store/tollFreeStepsStore'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { TollFreeAddNumberRoutesSteps } from 'pages/settings/pages/compliance/pages/tollFree/route/type'

import { PaymentCommonStore } from 'pages/settings/ui/PaymentCommon/store/paymentCommonStore'

export class AddTollFreeNumberStore {
  private readonly _assignNumberStore: AssignNumberCommonStore
  private readonly _chooseNumberTabsStore: ChooseNumberTabsStore
  private readonly _paymentStore: PaymentCommonStore

  private _modalId = nanoid()
  private _returnToRootAfterAdd = true

  constructor(private _tollFreeStepsStore: TollFreeStepsStore, private _entities: IEntitiesStore) {
    this._assignNumberStore = new AssignNumberCommonStore(
      {
        nextClickHandler: this.addNumberNextClick,
      },
      this._entities
    )
    this._chooseNumberTabsStore = new ChooseNumberTabsStore(this._entities)
    this._paymentStore = new PaymentCommonStore(this.chooseNumberTabsStore.refresh, this._entities)

    this.init()

    makeAutoObservable(this)
  }

  get chooseNumberTabsStore() {
    return this._chooseNumberTabsStore
  }

  get assignNumberStore() {
    return this._assignNumberStore
  }

  get paymentStore() {
    return this._paymentStore
  }

  get dropdownCardsStore() {
    return this._paymentStore.dropdownCardsStore
  }

  get selectedNumber() {
    return this.chooseNumberTabsStore.selectedNumber
  }

  get returnToRootAfterAdd() {
    return this._returnToRootAfterAdd
  }

  get tollFreeStepsStore() {
    return this._tollFreeStepsStore
  }

  get assignedInboxName() {
    if (this.assignNumberStore.assignType === 'existing') {
      const { sharedInboxes } = this._entities.inboxesStore

      return sharedInboxes.find((inbox) => inbox.id === this.assignNumberStore.activeInboxId)?.name
    }

    if (this.assignNumberStore.assignType === 'new') {
      return this.assignNumberStore.newInboxStore.newInboxName
    }

    return null
  }

  init = () => {
    this.chooseNumberTabsStore.setActiveTab(ITabsEnum.TOLL_FREE)

    this._entities.complianceStore.loadNumberVendors()
  }

  setReturnToRootAfterAdd = (isReturn: boolean) => {
    this._returnToRootAfterAdd = isReturn
  }

  openAddNumberModal = () => {
    const { tollFreeNumbers } = this._entities.numbersStore

    const title = tollFreeNumbers.length ? 'Add toll-free number' : 'Buy toll-free number'
    const desc = tollFreeNumbers.length
      ? 'To access toll-free verification, you first need to add a toll-free number.'
      : 'To access toll-free verification, you first need to buy a toll-free number.'
    const primaryActionText = tollFreeNumbers.length ? 'Add number' : 'Buy number'

    modalStore.addModal({
      id: this._modalId,
      width: 280,
      type: ModalTypeList.INFO,
      title,
      desc,
      primaryAction: {
        text: primaryActionText,
        onAction: () => {
          modalStore.removeModal(this._modalId)

          uiStore.changeRoute({
            path: `/${SettingsPath.compliance.tollFree.addNumber}/${TollFreeAddNumberRoutesSteps.ChooseNumber}`,
          })
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => modalStore.removeModal(this._modalId),
      },
    })
  }

  addNumberHandler = () => {
    const { tollFreeUnverifiedNumbers } = this.tollFreeStepsStore.complianceLayerStore
    this.setReturnToRootAfterAdd(false)

    if (!tollFreeUnverifiedNumbers.length) {
      if (this._entities.subscriptionStore.isTrial) {
        window.publicApiVue?.onCheckToolFreeNumbers()
      } else {
        this.openAddNumberModal()
      }
    } else {
      if (tollFreeUnverifiedNumbers.length === 1) {
        const unverifiedNumber = tollFreeUnverifiedNumbers[0]

        this.tollFreeStepsStore.complianceLayerStore.settingsStore.setTollFreePreAssignedNumber({
          id: unverifiedNumber.id,
          label: unverifiedNumber.formatted_number || '',
          data: {
            formatted_number: unverifiedNumber.formatted_number,
            numberable: {
              name: unverifiedNumber.numberable?.name || '',
            },
            number: unverifiedNumber.number,
            national_number: unverifiedNumber.national_number,
          },
        })
      }
      uiStore.changeRoute({
        path: `/${SettingsPath.compliance.tollFree.addNumber}/${TollFreeAddNumberRoutesSteps.Numbers}`,
      })
    }
  }

  addNumberNextClick = () => {
    uiStore.changeRoute({
      path: this.tollFreeStepsStore.tollFreeAddNumberNavigationStore.getNextUrl,
    })
  }

  resetStores = () => {
    this.assignNumberStore.reset()
    this.chooseNumberTabsStore.reset()
    this.paymentStore.combinedPriceInfoStore.reset()
  }

  afterAddNumberHandler = (phone: IResponseNumber, assignedInboxName?: string | null) => {
    toastStore.add({
      type: 'success',
      title: 'Number purchased',
    })
    this.tollFreeStepsStore.tollFreeAddNumberNavigationStore.setCurrentStep(1)
    this.resetStores()

    this._entities.complianceStore.setUnverifiedTollFreeNumber(new Phone(phone, this._entities))

    if (!this.returnToRootAfterAdd) {
      this.tollFreeStepsStore.submitForReviewClick(phone.id)

      return
    }

    this.tollFreeStepsStore.setStepLoading(false)

    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.tollFree.root}`,
    })

    setTimeout(() => {
      this.tollFreeStepsStore.complianceLayerStore.settingsStore.openVerifyNumberModal(
        phone,
        assignedInboxName || ''
      )
    }, 500)
  }

  confirmNumber = async () => {
    const { newInboxStore } = this.assignNumberStore
    if (!this.selectedNumber) return

    try {
      this.tollFreeStepsStore.setStepLoading(true)
      const number_vendor_id = this._entities.complianceStore.numberVendorId

      if (
        this.assignNumberStore.assignType === 'existing' &&
        this.assignNumberStore.activeInboxId
      ) {
        const { data } = await ComplianceApi.addTollFreeNumberToExistingInbox(
          this.assignNumberStore.activeInboxId,
          {
            members: null,
            name: '',
            number: this.selectedNumber.number,
            number_vendor_id,
            owner: {
              id: null,
            },
            vendor_id: '',
            is_mobile: null,
            payment_method: this.dropdownCardsStore.activeId,
          }
        )

        if (data) {
          const targetNumber = data.find((item) => item.number === this.selectedNumber?.number)

          targetNumber && this.afterAddNumberHandler(targetNumber, this.assignedInboxName)
        }
      }

      if (this.assignNumberStore.assignType === 'new') {
        const { data } = await ComplianceApi.addTollFreeNumberWithNewInbox({
          members: newInboxStore.selectedMembers.map((item) => String(item.id)),
          name: newInboxStore.newInboxName,
          number: this.selectedNumber.number,
          number_vendor_id,
          owner: {
            id:
              this._entities.usersStore.users.length === 1
                ? this._entities.usersStore.users[0].id
                : Number(newInboxStore.activeInboxOwner),
          },
          vendor_id: '',
          is_mobile: null,
          payment_method: this.dropdownCardsStore.activeId,
        })

        if (data) {
          this.afterAddNumberHandler(data.number, this.assignedInboxName)
        }
      }

      if (this.assignNumberStore.assignType === 'later') {
        const { data } = await PhoneApi.createNumbersUnassigned({
          busy: true,
          successful: false,
          original: {
            number: '',
            number_vendor_id: '',
            vendor_id: '',
            name: '',
            members: null,
            owner: {
              id: null,
            },
          },
          persistChanges: false,
          useEmptyField: true,
          number: this.selectedNumber.number,
          number_vendor_id,
          vendor_id: '',
          name: '',
          members: null,
          owner: {
            id: null,
          },
          errors: {
            errors: {},
          },
          is_mobile: null,
          payment_method: this.dropdownCardsStore.activeId,
        })

        if (data) {
          this.afterAddNumberHandler(data)
        }
      }
    } catch (e) {
      logger.error(e)
      this.tollFreeStepsStore.setStepLoading(false)
    }
  }
}
